import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButton from '@/shared/components/IconButton/IconButton'
import Select from '@/shared/components/Select/Select'
import { OptionType } from '@/shared/api/BaseAPI.model'
import { useGoogleFonts } from '../../hooks/useGoogleFonts'
import { FONT_FAMILIES, resolveFonts, stackFor } from '../../render/fonts'
import { Theme } from '../../render/theme'
import FontSpecimen from '../PropertiesPanel/FontSpecimen'
import styles from './FontFamilyFields.module.scss'

interface FontFamilyFieldsProps {
  theme: Theme
  onChange: (theme: Theme) => void
}

function search(query: string): string[] {
  const needle = query.trim().toLowerCase()
  const names = FONT_FAMILIES.map(item => item.family)
  if (!needle) return names

  /*
    Everything that matches, in popularity order, with the families whose name
    begins with what was typed first. No cap: a row costs nothing until it is
    scrolled into view, which is when it asks for its own face.
  */
  const starts = names.filter(name => name.toLowerCase().startsWith(needle))
  const contains = names.filter(
    name =>
      !name.toLowerCase().startsWith(needle) &&
      name.toLowerCase().includes(needle)
  )
  return [...starts, ...contains]
}

/**
 * The two faces of a page, chosen one at a time out of the whole catalogue,
 * with the pair they came from as the way back.
 *
 * Choosing a family for the headings or for the body overrides the pair for
 * that half and leaves the other alone, which is what lets somebody keep a body
 * face that works and try six headings against it. The undo beside a field puts
 * that half back to the pair's own face; with neither half overridden the whole
 * override is dropped, so the theme says "this pair" again rather than "this
 * pair, and also exactly what it already is".
 *
 * Shared by the settings form's advanced block and by the builder's panel, so
 * the two cannot answer the same question differently.
 */
function FontFamilyFields({ theme, onChange }: FontFamilyFieldsProps) {
  const { t } = useTranslation()
  const [headingQuery, setHeadingQuery] = useState('')
  const [bodyQuery, setBodyQuery] = useState('')

  const chosen = resolveFonts(theme)
  const headingOptions = useMemo(() => search(headingQuery), [headingQuery])
  const bodyOptions = useMemo(() => search(bodyQuery), [bodyQuery])

  // The two current choices, drawn in themselves right here. Every other family
  // in the lists asks for itself when it is first scrolled into view.
  useGoogleFonts([chosen.heading, chosen.body])

  const option = (family: string): OptionType => ({
    value: family,
    label: family
  })

  const setFamily = (role: 'heading' | 'body', family: string | undefined) => {
    const fonts = { ...theme.fonts, [role]: family }
    const empty = !fonts.heading && !fonts.body
    onChange({ ...theme, fonts: empty ? undefined : fonts })
  }

  const field = (
    role: 'heading' | 'body',
    label: string,
    options: string[],
    onQuery: (value: string) => void
  ) => (
    <div className={styles.row}>
      <Select
        label={label}
        options={options.map(option)}
        value={option(chosen[role])}
        onSearch={onQuery}
        renderOptionLabel={item => <FontSpecimen family={item.value} />}
        onChange={value => {
          if (!value || Array.isArray(value)) return
          setFamily(role, value.value)
        }}
        focused
        hideClearIcon
      />
      {theme.fonts?.[role] && (
        <IconButton
          icon="las la-undo icon-medium"
          color="primary"
          ariaLabel={t('WEBSITE.FONTS.RESET')}
          onClick={() => setFamily(role, undefined)}
        />
      )}
    </div>
  )

  return (
    <div className={styles.fields}>
      {field('heading', t('WEBSITE.FONTS.HEADING'), headingOptions, setHeadingQuery)}
      {field('body', t('WEBSITE.FONTS.BODY'), bodyOptions, setBodyQuery)}

      {/*
        The two faces together, at a size worth judging. The fields above name
        the choice and the list shows each family alone; neither answers the
        only question that matters, which is whether these two look right beside
        each other. Not a button: there is nothing to pick here.
      */}
      <div className={styles.preview}>
        <span
          className={styles.previewHeading}
          style={{ fontFamily: stackFor(chosen.heading) }}
        >
          {t('WEBSITE.FONTS.SAMPLE_HEADING')}
        </span>
        <span
          className={styles.previewBody}
          style={{ fontFamily: stackFor(chosen.body) }}
        >
          {t('WEBSITE.FONTS.SAMPLE_BODY')}
        </span>
        {/*
          Digits and a currency, because a menu is mostly prices and that is
          where two faces differ most visibly.
        */}
        <span
          className={styles.previewBody}
          style={{ fontFamily: stackFor(chosen.body) }}
        >
          {t('WEBSITE.FONTS.SAMPLE_PRICE')}
        </span>
      </div>
    </div>
  )
}

export default FontFamilyFields
